/**
 * Vibe Coaching Panels — Youth-Adapted Safety UI
 *
 * Adapted from the adult coaching panels with:
 *  - Always-visible crisis footer: the reader's own country's youth lines,
 *    from the fleet's shared table, with a country picker
 *  - Age-adapted consent gate (simpler language, app-only sharing, AI acknowledgement)
 *  - Cooldown countdown display
 *  - No rehearsal mode references
 *
 * Australian English throughout.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CRISIS_SUPPORT, COUNTRY_KEYS, initialCountry, writeCountryPref, serviceById } from '../../data/crisisSupport';
import {
  AlertTriangle, Phone, MessageCircle, ExternalLink, Heart, ShieldCheck,
  Info, ChevronDown, ChevronUp, Clock
} from 'lucide-react';
import { coachCardLines, textLineSmsHref } from '../../../../../packages/crisis-support/src/textLines.js';

// --- Crisis Resources (always visible in Vibe) ---
// Numbers come from the shared seven-market table (data/crisisSupport.js).
// The reader's country is the one they chose in Panic SOS or on Resources,
// else the browser's region, else Australia as the worked example — always
// labelled with the country, always changeable, never another country's
// numbers presented as theirs.

const dialable = (number) => {
  const raw = String(number || '').replace(/^Triple Zero \((.*)\)$/, '$1');
  return /^[\d\s()+-]+$/.test(raw) ? raw.replace(/[^\d+]/g, '') : null;
};

export function CrisisResourcesCard({ resources }) {
  const displayResources = resources?.length ? resources : null;
  if (!displayResources) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border-2 border-red-400 bg-red-50 p-5 shadow-lg"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="flex-shrink-0 mt-1 text-red-600" size={22} />
        <div className="flex-1">
          <h3 className="text-base sm:text-lg font-semibold text-red-900">
            Please talk to someone right now
          </h3>
          <p className="text-sm sm:text-base text-red-800 mt-1 leading-relaxed">
            What you're sharing is really important, and you deserve to talk to a real person about it. These services are free and confidential.
          </p>
          <ul className="mt-3 space-y-2">
            {coachCardLines(displayResources).map((r) => (
              <li key={r.name} className="text-sm sm:text-base">
                <span className="font-semibold text-red-900">{r.name}</span>
                <span className="text-red-800"> — {r.detail}</span>
                {r.phone && (
                  <a href={`tel:${r.phone.replace(/\s/g, '')}`}
                    className="ml-2 inline-flex items-center gap-1 font-bold text-red-700 hover:text-red-900">
                    <Phone size={14} /> {r.phone}
                  </a>
                )}
                {r.text && (textLineSmsHref(r.text) ? (
                  <a href={textLineSmsHref(r.text)} className="ml-2 inline-flex items-center gap-1 font-bold text-red-700 hover:text-red-900">
                    <MessageCircle size={14} /> {r.text}
                  </a>
                ) : (
                  <span className="ml-2 inline-flex items-center gap-1 font-bold text-red-700">
                    <MessageCircle size={14} /> {r.text}
                  </span>
                ))}
                {r.url && (
                  <a href={r.url} target="_blank" rel="noreferrer"
                    className="ml-2 inline-flex items-center gap-1 text-red-700 hover:text-red-900">
                    <ExternalLink size={14} />
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Always-visible crisis footer for Vibe apps.
 * Shows youth-specific resources at the bottom of every coaching session.
 */
export function YouthCrisisFooter() {
  const [country, setCountry] = useState(initialCountry);
  const data = CRISIS_SUPPORT[country];
  if (!data) return null;
  // Youth services first, then the rest, three at most; the emergency number always.
  const services = [...data.services.filter((s) => s.youthFocused), ...data.services.filter((s) => !s.youthFocused)].slice(0, 3);
  const choose = (key) => { setCountry(key); writeCountryPref(key); };
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3" data-crisis-footer={country}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <p className="text-xs sm:text-sm text-slate-600 font-medium">
          Need to talk to a real person? Free and confidential, in {data.name}:
        </p>
        <label className="text-xs text-slate-500 flex items-center gap-1">
          <span>Not in {data.name}?</span>
          <select
            value={country}
            onChange={(e) => choose(e.target.value)}
            aria-label="Choose your country for crisis numbers"
            className="text-xs border border-slate-300 rounded px-1 py-0.5 bg-white text-slate-700"
          >
            {COUNTRY_KEYS.map((key) => (
              <option key={key} value={key}>{CRISIS_SUPPORT[key].name}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs sm:text-sm">
        <span className="text-slate-700">
          <span className="font-medium">Emergency</span>
          <span className="ml-1 text-red-700 font-semibold">{data.emergency.number}</span>
        </span>
        {services.map((r) => (
          <span key={r.name} className="text-slate-700">
            <span className="font-medium">{r.name}</span>
            {dialable(r.number) ? (
              <a href={`tel:${dialable(r.number)}`} className="ml-1 text-blue-700 hover:text-blue-900 font-medium">
                {r.number}
              </a>
            ) : (
              <span className="ml-1 text-blue-700 font-medium">{r.number}</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

// --- Professional Support Cards ---

export function ProfessionalSupportCard({ support }) {
  if (!support) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-amber-300 bg-amber-50 p-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <Heart className="flex-shrink-0 mt-1 text-amber-600" size={20} />
        <div className="flex-1">
          <h4 className="font-semibold text-amber-900 text-sm sm:text-base">
            Worth chatting to: {support.type}
          </h4>
          <p className="text-sm sm:text-base text-amber-800 mt-1 leading-relaxed">
            {support.rationale}
          </p>
          {support.howToFind?.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm sm:text-base text-amber-800 list-disc pl-5">
              {support.howToFind.map((hint, i) => <li key={i}>{hint}</li>)}
            </ul>
          )}
          <p className="text-xs sm:text-sm text-amber-700 mt-3 italic">
            Lots of people your age talk to someone — it's a smart move, not a big deal.
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export function SubtleProfessionalHint({ support }) {
  const [expanded, setExpanded] = useState(false);
  if (!support) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-lg border border-blue-200 bg-blue-50/60 px-4 py-3"
    >
      <div className="flex items-start gap-2">
        <Info className="flex-shrink-0 mt-0.5 text-blue-500" size={16} />
        <div className="flex-1 text-sm sm:text-base text-blue-900 leading-relaxed">
          <p>{support.rationale}</p>
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-1.5 flex items-center gap-1 text-xs sm:text-sm font-medium text-blue-700 hover:text-blue-900"
          >
            How to find someone to talk to
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {expanded && support.howToFind?.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs sm:text-sm text-blue-800 list-disc pl-4">
              {support.howToFind.map((hint, i) => <li key={i}>{hint}</li>)}
            </ul>
          )}
          <p className="text-xs text-blue-600/80 mt-2 italic">{support.disclaimer}</p>
        </div>
      </div>
    </motion.div>
  );
}

// --- Usage + Disclaimer ---

export function UsageIndicator({ usage }) {
  if (!usage) return null;
  const { tier, used, dailyLimit, remaining, upgradeUrl } = usage;
  const pct = dailyLimit ? Math.min(100, (used / dailyLimit) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600">
      <div className="flex-1 max-w-[120px] h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full ${pct >= 80 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
      </div>
      <span>{used}/{dailyLimit} today</span>
      {tier === 'free' && upgradeUrl && remaining <= 2 && (
        <a href={upgradeUrl} className="ml-2 font-medium text-blue-700 hover:text-blue-900 underline">
          Upgrade
        </a>
      )}
    </div>
  );
}

export function CoachingDisclaimer() {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg text-xs sm:text-sm text-slate-600 leading-snug">
      <Info className="flex-shrink-0 text-slate-400" size={15} />
      <span>
        This is an AI coach, not a real person.{' '}
        <span className="font-medium text-slate-700">It can't replace talking to someone you trust.</span>
      </span>
    </div>
  );
}

// --- Cooldown Countdown ---

export function CooldownCountdown({ cooldownEnd }) {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (!cooldownEnd) { setRemaining(0); return; }
    const tick = () => {
      const ms = Math.max(0, cooldownEnd - Date.now());
      setRemaining(ms);
      if (ms <= 0) return;
      return requestAnimationFrame(tick);
    };
    const id = tick();
    return () => { if (id) cancelAnimationFrame(id); };
  }, [cooldownEnd]);

  if (remaining <= 0) return null;
  const secs = Math.ceil(remaining / 1000);

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg text-sm text-blue-800">
      <Clock className="flex-shrink-0 text-blue-500" size={16} />
      <span>Take a moment to think about that. You can message again in <strong>{secs}s</strong></span>
    </div>
  );
}

// --- Youth-Adapted Consent Gate ---

const COUNTRIES = [
  { code: 'AU', label: 'Australia' },
  { code: 'NZ', label: 'New Zealand' },
  { code: 'IN', label: 'India' },
  { code: 'UK', label: 'United Kingdom' },
  { code: 'IE', label: 'Ireland' },
  { code: 'CA', label: 'Canada' },
  { code: 'US', label: 'United States' }
];

export function ConsentGate({ onAccept, onDecline }) {
  const [firstName, setFirstName] = useState('');
  const [country, setCountry] = useState('');
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!ack) return;
    setSubmitting(true);
    await onAccept({
      personalContext: {
        firstName: firstName.trim(),
        country: country || null,
        preferredCoachingStyle: 'supportive',
        communicationPreference: 'balanced',
        pace: 'moderate'
      },
      dataSharingScope: 'app-only'
    });
    setSubmitting(false);
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg p-6 sm:p-8">
      <div className="flex items-center gap-3 mb-4">
        <ShieldCheck className="text-blue-600" size={28} />
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Before we start</h2>
      </div>
      <div className="space-y-3 text-sm sm:text-base text-slate-700 leading-relaxed">
        <p>
          This coach is an <strong>AI — not a real person</strong>. It can help you think things through,
          but it can't replace talking to someone you trust, like a friend, family member, teacher, or counsellor.
        </p>
        <p>
          <strong>What it remembers:</strong> your name, things you talk about, and patterns it notices over time.
          You can see, change, or delete everything it knows about you at any time.
        </p>
        <p>
          <strong>If things get heavy:</strong> the coach will always point you to real people who can help —
          like Kids Helpline ({serviceById('au-kids-helpline').number}) in Australia, or the young people&apos;s helpline where you live. It won't try to be a therapist.
        </p>
        <p className="text-slate-600 italic">
          What the coach remembers is stored in Australia. To write each reply, your message is sent to
          Anthropic&apos;s Claude, which doesn&apos;t use it to train AI. We never sell your data or share it with
          advertisers.
        </p>
      </div>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">What should I call you?</label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value.slice(0, 40))}
            placeholder="First name or nickname"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            maxLength={40}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Where are you based? <span className="text-slate-500 font-normal">(so we show the right support resources)</span>
          </label>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select a country</option>
            {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </div>

        <label className="flex items-start gap-3 cursor-pointer p-3 bg-slate-50 rounded-lg border border-slate-200">
          <input
            type="checkbox"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-slate-700 leading-relaxed">
            I understand this is an AI coach, not a real person. If I need real help,
            I'll reach out to a trusted adult or one of the support services.
          </span>
        </label>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            type="submit"
            disabled={submitting || !ack}
            className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-60"
          >
            {submitting ? 'Setting up…' : "Got it — let's go"}
          </button>
          {onDecline && (
            <button
              type="button"
              onClick={onDecline}
              className="px-4 py-3 text-slate-600 hover:text-slate-900 font-medium"
            >
              Maybe later
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
